import {
    Box,
    Button,
    Heading,
    ListItem,
    OrderedList,
    Text,
    useToast
} from '@chakra-ui/react'
import { Layout, Logo } from 'components/atoms'
import { useAuthContext } from 'contexts/auth'
import { AuthDomain } from 'domains/AuthDomain'
import React, { useState } from 'react'
import {
    Link as LinkRouter,
    useNavigate
} from 'react-router-dom'
import AuthService from 'services/auth'

const Certificado: React.FC = () => {
    const toast = useToast()
    const navigate = useNavigate()

    const {
        setCurrentUser,
        setLoadingAuth
    } = useAuthContext()

    const [isLoading, setIsLoading] =
        useState(false)

    const conectarAssinador = async () => {
        const client = window.SerproSignerClient

        if (!client) {
            throw new Error(
                'Assinador Digital SERPRO não encontrado. Verifique se o aplicativo e a extensão estão instalados.'
            )
        }

        if (client.isConnected()) {
            return client
        }

        await new Promise<void>(
            (resolve, reject) => {
                client.connect(
                    () => resolve(),
                    () => reject(
                        new Error(
                            'A conexão com o assinador foi encerrada.'
                        )
                    ),
                    (error: any) => reject(error)
                )
            }
        )

        return client
    }

    const assinarTexto = async (
        texto: string
    ): Promise<string> => {
        const client = await conectarAssinador()

        return new Promise<string>(
            (resolve, reject) => {
                client
                    .sign(
                        'TEXT',
                        texto,
                        'UTF-8',
                        'BASE64',
                        false
                    )
                    .success((response: any) => {
                        const assinatura =
                            response?.signature ||
                            response

                        if (
                            typeof assinatura !==
                                'string' ||
                            !assinatura.trim()
                        ) {
                            reject(
                                new Error(
                                    'O assinador não retornou a assinatura digital.'
                                )
                            )
                            return
                        }

                        resolve(assinatura)
                    })
                    .error((error: any) => {
                        reject(error)
                    })
            }
        )
    }

    const obterMensagemErro = (
        error: any
    ): string => {
        const codigo =
            error?.response?.data?.[0]?.codigo

        if (codigo === '0024') {
            return 'O desafio expirou ou já foi utilizado. Tente novamente.'
        }

        if (
            codigo === '0023' ||
            codigo === '0025'
        ) {
            return 'O certificado não é válido para este usuário ou instituição financeira.'
        }

        if (typeof error === 'string') {
            return error
        }

        if (error?.message) {
            return error.message
        }

        if (error?.error) {
            return error.error
        }

        return 'Não foi possível realizar a autenticação com o certificado digital.'
    }

    const handleConnectCertificate =
        async () => {
            setIsLoading(true)
            setLoadingAuth(true)

            try {
                const challengeResponse =
                    await AuthService
                        .createCertificateChallenge()

                const {
                    desafioId,
                    texto
                } = challengeResponse.data

                const assinaturaBase64 =
                    await assinarTexto(texto)

                const authDomain =
                    new AuthDomain()

                const tokens =
                    await authDomain
                        .signinWithCertificate(
                            desafioId,
                            assinaturaBase64
                        )

                setCurrentUser(tokens)

                sessionStorage.removeItem(
                    'attempts'
                )

                toast({
                    title:
                        'Autenticação realizada!',
                    description:
                        'Certificado digital validado com sucesso.',
                    status: 'success',
                    position: 'bottom-left',
                    isClosable: true
                })

                navigate('/instituicoes')
            } catch (error: any) {
                console.warn(
                    'Falha na autenticação por certificado:',
                    error
                )

                toast({
                    title: 'Acesso negado',
                    description:
                        obterMensagemErro(error),
                    status: 'error',
                    position: 'bottom-left',
                    duration: 7000,
                    isClosable: true
                })
            } finally {
                setIsLoading(false)
                setLoadingAuth(false)
            }
        }

    return (
        <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            color="blackAlpha.900"
            bgColor="backdrop"
            minH="100vh"
            w="100%"
            px={4}
        >
            <Box
                display="grid"
                w="100%"
                maxW="lg"
                gap={8}
            >
                <Box>
                    <Logo maxW={187} />
                </Box>

                <Box
                    bg="white"
                    borderRadius="lg"
                    px={{ base: 6, md: 12 }}
                    py={{ base: 8, md: 12 }}
                >
                    <Heading
                        size="md"
                        textAlign="center"
                        mb={2}
                    >
                        Acesso com Certificado Digital
                    </Heading>

                    <Text
                        fontSize="lg"
                        textAlign="left"
                        mt={8}
                    >
                        Para acessar, utilize um
                        certificado ICP-Brasil A1 ou
                        A3.
                    </Text>

                    <OrderedList
                        mt={4}
                        spacing={2}
                        pl={4}
                    >
                        <ListItem>
                            Instale e inicialize o
                            Assinador Digital SERPRO.
                        </ListItem>

                        <ListItem>
                            Para certificado A3,
                            conecte o token ou
                            smartcard.
                        </ListItem>

                        <ListItem>
                            Clique no botão abaixo e
                            selecione o certificado.
                        </ListItem>
                    </OrderedList>

                    <Layout
                        type="container"
                        full
                        px={6}
                        mt={10}
                    >
                        <Layout
                            type="row"
                            gutter={{ y: 1.5 }}
                        >
                            <Layout
                                type="col"
                                layout={{ base: 12 }}
                            >
                                <Button
                                    w="full"
                                    colorScheme="primary"
                                    size="md"
                                    onClick={
                                        handleConnectCertificate
                                    }
                                    isLoading={
                                        isLoading
                                    }
                                    loadingText="Validando certificado..."
                                >
                                    Autenticar com
                                    Certificado Digital
                                </Button>
                            </Layout>

                            <Layout
                                type="col"
                                layout={{ base: 12 }}
                            >
                                <Button
                                    as={LinkRouter}
                                    to="/entrar"
                                    size="md"
                                    w="full"
                                    colorScheme="primary"
                                    variant="link"
                                    textDecoration="underline"
                                    isDisabled={
                                        isLoading
                                    }
                                >
                                    Voltar
                                </Button>
                            </Layout>
                        </Layout>
                    </Layout>
                </Box>
            </Box>
        </Box>
    )
}

export default Certificado